import { Col, DatePicker, Form, Row, Select } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import queryKeys from "common/queryKey";
import { CALL_FEATCH_API } from "common/API";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilter } from "redux/actions";
import {
  getClientid,
  getFilters,
  getManagingOffice,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import { utils, writeFileXLSX } from "xlsx";

const RoundedSelect = styled(Select)`
  &:where(.ant-select) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select-focused) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select) .ant-select-selector:hover {
    border-radius: 15px !important;
  }
`;

// Annual Leave category the closing balance is reported for
const ANNUAL_LEAVE_CATEGORY_ID = "9";

const displayValue = (val) =>
  val === undefined || val === null || val === "" || val === "null" ? "-" : val;

function ClosingLeaveBalance() {
  const userList = useSelector((state) => getUserList(state));
  const clientId = useSelector(getClientid);
  const managingOfficeList = useSelector(getManagingOffice);
  const userInfo = useSelector(getUserInfo);
  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);

  const isSuperAdmin = userInfo?.roleId === "2";

  const [form] = Form.useForm();
  const filters = useSelector(getFilters);
  const menuName = "closingleavebalance";
  const initialFilter = filters[menuName];
  const dispatch = useDispatch();

  const [selectedYear, setSelectedYear] = useState(
    initialFilter?.year || moment().format("YYYY")
  );

  const activeUsers = useMemo(() => {
    return userList?.filter((user) => user?.statusid === "1");
  }, [userList]);

  const selectedManagingOffice = isSuperAdmin
    ? filters?.closingleavebalance?.managingoffice ||
      finduser?.managingoffice?.[0] ||
      managingOfficeList?.[0]
    : finduser?.managingoffice?.[0];

  useEffect(() => {
    if (
      isSuperAdmin &&
      !filters?.closingleavebalance?.managingoffice &&
      finduser?.managingoffice?.[0]
    ) {
      const defaultOffice = finduser.managingoffice[0];
      dispatch(setFilter(menuName, "managingoffice", defaultOffice));
      form.setFieldsValue({ managingoffice: defaultOffice });
    }
  }, [isSuperAdmin, finduser]);

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    dispatch(setFilter(menuName, "name", values.name || []));
    dispatch(
      setFilter(menuName, "managingoffice", values.managingoffice || null)
    );
    if (values.year) {
      const year = moment(values.year).format("YYYY");
      dispatch(setFilter(menuName, "year", year));
      setSelectedYear(year);
    }
  };

  const { getClosingLeaveBalance } = queryKeys;
  const { url } = getClosingLeaveBalance(clientId, selectedYear);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const fetchClosingLeaveBalance = async () => {
      setIsLoading(true);
      const response = await CALL_FEATCH_API(url, "post", {
        startDate: `${selectedYear}-01-01`,
        endDate: `${selectedYear}-12-31`,
        leaveCategoryId: ANNUAL_LEAVE_CATEGORY_ID,
      });
      setData(response);
      setIsLoading(false);
    };
    fetchClosingLeaveBalance();
  }, [url, selectedYear]);

  // Leave figures, employee details and the supervisor come from the API;
  // the user list is used to keep active users only, for the managing office
  // filter, and as a fallback for the employee details.
  const dataList = useMemo(() => {
    if (!data || !activeUsers?.length) return [];
    const usersById = new Map(activeUsers.map((u) => [u.userid, u]));
    const usersByEmployeeId = new Map(
      userList?.map((u) => [u.employeeid, u]) || []
    );

    const responseData =
      data?.annualLeaveClosingBalanceList ||
      data?.response?.annualLeaveClosingBalanceList ||
      [];

    return responseData
      .filter((item) => usersById.has(item?.userId))
      .map((item) => {
        const user = usersById.get(item.userId);
        const supervisor = usersByEmployeeId.get(user?.OfficeSupervisorId);
        return {
          userid: item.userId,
          fullname: item.employeeName?.trim() || user?.fullname,
          employeeid: item.employeeId || user?.employeeid,
          annualLeaveConsumed: item.annualLeaveConsumed,
          availableLeaveBalance: item.availableAnnualLeaveBalance,
          carryForwardDays: item.daysToBeCarriedForward,
          forfeitedDays: item.daysForfeited,
          officesupervisor:
            item.officeSupervisor?.trim() || supervisor?.fullname,
          managingoffice: user?.managingoffice?.[0],
        };
      })
      .filter(
        (item) =>
          selectedManagingOffice === "Head Office" ||
          item.managingoffice === selectedManagingOffice
      );
  }, [data, activeUsers, userList, selectedManagingOffice]);

  const userOptions = (
    selectedManagingOffice === "Head Office"
      ? activeUsers
      : activeUsers?.filter(
          (user) => user?.managingoffice?.[0] === selectedManagingOffice
        )
  )?.map((user) => ({
    label: user?.fullname,
    value: user?.userid,
  }));

  const managingOfficeOption = managingOfficeList?.map((val) => ({
    label: val,
    value: val,
  }));

  const getFilteredData = () => {
    const name = form.getFieldValue("name") || [];
    if (name?.length > 0) {
      return dataList?.filter((item) => name?.includes(item?.userid));
    }
    return dataList;
  };

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "fullname",
      key: "fullname",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
    {
      title: "Employee ID",
      dataIndex: "employeeid",
      key: "employeeid",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
    {
      title: "Annual Leave Consumed",
      dataIndex: "annualLeaveConsumed",
      key: "annualLeaveConsumed",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
    {
      title: "Available Annual Leave Balance",
      dataIndex: "availableLeaveBalance",
      key: "availableLeaveBalance",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
    {
      title: "Days to be Carried Forward",
      dataIndex: "carryForwardDays",
      key: "carryForwardDays",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
    {
      title: "Days Forfeited",
      dataIndex: "forfeitedDays",
      key: "forfeitedDays",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
    {
      title: "Office Supervisor",
      dataIndex: "officesupervisor",
      key: "officesupervisor",
      className: "normal-column",
      render: (val) => <span>{displayValue(val)}</span>,
    },
  ];

  const xport = React.useCallback(() => {
    const exportData = getFilteredData().map((item) => ({
      "Employee Name": displayValue(item.fullname),
      "Employee ID": displayValue(item.employeeid),
      "Annual Leave Consumed": displayValue(item.annualLeaveConsumed),
      "Available Annual Leave Balance": displayValue(
        item.availableLeaveBalance
      ),
      "Days to be Carried Forward": displayValue(item.carryForwardDays),
      "Days Forfeited": displayValue(item.forfeitedDays),
      "Office Supervisor": displayValue(item.officesupervisor),
    }));

    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Closing Leave Balance");
    writeFileXLSX(wb, `Closing Leave Balance ${selectedYear}.csv`);
  });

  return (
    <div>
      <Row>
        <Col sm={24}>
          <div className="d-flex justify-content-between">
            <Title>Closing Leave Balance</Title>
            <ButtonOutlined
              className="text-end"
              disabled={getFilteredData()?.length === 0}
              onClick={xport}
            >
              Export CSV file
            </ButtonOutlined>
          </div>
        </Col>
        <Col sm={24}>
          <Form
            form={form}
            layout="vertical"
            className="mt-2"
            onValuesChange={handleFormChange}
            initialValues={{
              name: initialFilter?.name,
              managingoffice: initialFilter?.managingoffice,
              year: moment(selectedYear, "YYYY"),
            }}
          >
            <Row gutter={12} className="g-3 mb-2">
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item name="year">
                  <DatePicker
                    picker="year"
                    format="YYYY"
                    allowClear={false}
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "50px",
                      width: "100%",
                    }}
                    placeholder="Year"
                    disabledDate={(current) =>
                      current && current.year() > new Date().getFullYear()
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="name"
                  label="Employee Name"
                  options={userOptions}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
              {isSuperAdmin && (
                <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                  <Form.Item name="managingoffice">
                    <RoundedSelect
                      placeholder="Select managing office"
                      options={managingOfficeOption}
                      clearIcon={null}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={getFilteredData()}
        columns={columns}
        pagination={false}
        loading={isLoading}
        rowKey="userid"
        id="Table"
      />
    </div>
  );
}

export default ClosingLeaveBalance;
